import { Badge, Button, ExternalLinkIcon, Skeleton } from '@commander/ui';
import { useCommanderGoAnalytics } from '../../hooks/useCommanderGoAnalytics.js';
import type { ActionItem, ActionListBlock } from '../blocks/action-list.js';
import { ExpandableBlock, ExpandTrigger } from './ExpandableBlock.js';
import { sanitizeHref, trackDeepLinkClick } from './utils.js';

// --- Priority ---

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
  low: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
};

function getPriorityClasses(priority?: string): string {
  if (!priority) return 'bg-muted text-muted-foreground';
  return PRIORITY_COLORS[priority.toLowerCase()] ?? 'bg-muted text-muted-foreground';
}

function sortByPriority(actions: ActionItem[]): ActionItem[] {
  return [...actions].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority?.toLowerCase() ?? ''] ?? 3;
    const pb = PRIORITY_ORDER[b.priority?.toLowerCase() ?? ''] ?? 3;
    return pa - pb;
  });
}

// --- Single Action Item ---

function ActionItemRow({
  action,
  onDeepLinkClick,
}: {
  action: ActionItem;
  onDeepLinkClick?: (href: string) => void;
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium">{action.title}</p>
          {action.priority && (
            <span
              className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none ${getPriorityClasses(action.priority)}`}
            >
              {action.priority}
            </span>
          )}
        </div>
        {action.description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{action.description}</p>
        )}
        {action.metadata && Object.keys(action.metadata).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            {Object.entries(action.metadata).map(([key, val]) => (
              <span key={key} className="text-[10px] text-muted-foreground">
                <span className="font-medium">{key}:</span> {val}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Deep link */}
      {(() => {
        const safeHref = sanitizeHref(action.deep_link);
        return safeHref ? (
          <a href={safeHref} onClick={() => onDeepLinkClick?.(safeHref)} className="flex-shrink-0">
            <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-[10px]">
              {action.deep_link_label ?? 'View'}
              <ExternalLinkIcon className="h-3 w-3" />
            </Button>
          </a>
        ) : action.deep_link ? (
          <span className="flex-shrink-0">
            <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-[10px]" disabled>
              {action.deep_link_label ?? 'View'}
            </Button>
          </span>
        ) : null;
      })()}
    </div>
  );
}

// --- Action List Content ---

function ActionListContent({
  data,
  onDeepLinkClick,
}: {
  data: ActionListBlock;
  onDeepLinkClick?: (href: string) => void;
}) {
  const sorted = sortByPriority(data.actions);

  return (
    <div>
      <div className="px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-xs font-semibold">{data.title}</h4>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px]">
              {data.actions.length} action{data.actions.length !== 1 ? 's' : ''}
            </Badge>
            <ExpandTrigger />
          </div>
        </div>
      </div>
      <ul className="list-none divide-y px-3">
        {sorted.map((action, i) => (
          <li key={`${action.title}-${i}`}>
            <ActionItemRow action={action} onDeepLinkClick={onDeepLinkClick} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Main ActionList Component ---

export function ActionList({ data }: { data: ActionListBlock }) {
  const analyticsCtx = useCommanderGoAnalytics();
  const handleDeepLinkClick = (href: string) => {
    trackDeepLinkClick(analyticsCtx, 'action_list', href);
  };

  return (
    <ExpandableBlock
      title={data.title}
      expandedContent={<ActionListContent data={data} onDeepLinkClick={handleDeepLinkClick} />}
    >
      <div className="rounded-lg border bg-card text-card-foreground">
        <ActionListContent data={data} onDeepLinkClick={handleDeepLinkClick} />
      </div>
    </ExpandableBlock>
  );
}

// --- Skeleton ---

export function ActionListSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-3">
      <Skeleton className="h-3 w-[45%]" />
      <div className="mt-3 space-y-3">
        {['a', 'b', 'c'].map((id) => (
          <div key={id}>
            <div className="flex items-center gap-2">
              <Skeleton className="h-3 w-[60%]" />
              <Skeleton className="h-4 w-12 rounded-full" />
            </div>
            <Skeleton className="mt-1 h-2.5 w-[80%]" />
          </div>
        ))}
      </div>
    </div>
  );
}
